// What remains of the legacy BFF surface after batch 011.
//
// The transfer domain's fourteen endpoints are GONE — migrated wholesale to
// services/transfer.api.ts against the new contract. Their two dependants went
// with them: the legacy patient-group list route (the transfer pages were its
// last consumers; every other form already reads the organizations endpoint)
// and the patient-share pair (keyed by a numeric patient id the new system does
// not have, so they answered nothing but errors).
//
// Described rather than quoted, deliberately: the acceptance check for this
// migration is a grep for those legacy route strings, and a comment naming one
// would satisfy the grep it is supposed to be proving empty.
//
// The measurement projections below are batch 009/010 territory and are
// deliberately untouched here.
//
// ⚠️ DELETED 2026-08-26: the legacy acknowledgement mutation — the BFF's own ack
// POST, its exported hook, and the two request types that had no other user.
// Described rather than quoted, for the reason given above: naming the route or
// the hook literally here would satisfy the very grep that is supposed to prove
// they are gone. Three reasons, and the deletion needs all three:
//   1. ZERO CONSUMERS — the hook appeared only on its own export line; nothing
//      in src/ imported it.
//   2. Its route was the legacy BFF's, which the new backend does not serve, so
//      every call was going to answer 404.
//   3. The live acknowledgement surface is batch 010's, in
//      services/measurement.api.ts, already wired into
//      components/patient-measurement-data/.../measurement-header-cell.tsx.
// Same reasoning ADR-B5 used to drop six dead endpoints in this domain: a reader
// cannot tell an unreachable route nobody calls apart from a live one. It also
// removes the repo's only unguarded `await queryFulfilled` (the rule is
// .trellis/spec/frontend/08-organization-fko-sync.md:57-59) — by deletion rather
// than by a try/catch wrapped around code no caller can reach.
import {
  ApiTags,
  GroupedMeasurementResponse,
  LatestMeasurementResponse,
  MeasurementRequest,
  MeasurementResponse,
} from '@/services/api.types';
import { createApi } from '@reduxjs/toolkit/query/react';
import authedQuery from '@/services/authed.query';

export const clinicianApi = createApi({
  reducerPath: 'clinicianApi',
  tagTypes: [ApiTags.PatientMeasurementStatus],
  baseQuery: authedQuery,
  endpoints: builder => ({
    getMeasurementResultsByPatientId: builder.query<
      MeasurementResponse,
      MeasurementRequest
    >({
      query: (request: MeasurementRequest) => ({
        url: `/clinician/dashboard/patient/${request.patient_id}/mea`,
        params: {
          from: request.from,
          to: request.to,
        },
      }),
      providesTags: (result, error, { patient_id }) => [
        { type: ApiTags.PatientMeasurementStatus, id: patient_id },
      ],
    }),
    getGroupedMeasurementResultsByPatientId: builder.query<
      GroupedMeasurementResponse,
      MeasurementRequest
    >({
      query: (request: MeasurementRequest) => ({
        url: `/clinician/dashboard/patient/${request.patient_id}/grouped-mea`,
        params: {
          from: request.from,
          to: request.to,
        },
      }),
      providesTags: (result, error, { patient_id }) => [
        { type: ApiTags.PatientMeasurementStatus, id: patient_id },
      ],
    }),
    getLatestResultsByPatientId: builder.query<
      LatestMeasurementResponse,
      { patient_id: number }
    >({
      query: (request: { patient_id: number }) => ({
        url: `/clinician/dashboard/patients/${request.patient_id}/latest/mea`,
      }),
    }),
  }),
});

export const {
  useGetMeasurementResultsByPatientIdQuery,
  useGetGroupedMeasurementResultsByPatientIdQuery,
  useGetLatestResultsByPatientIdQuery,
} = clinicianApi;

export default clinicianApi;
